import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Plus, Ban, ShoppingBag, Sparkles, Building2, KeyRound, Mail, Rocket, BarChart3, ExternalLink, Check, X, Settings, Download, TrendingUp } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid, Legend } from "recharts";
import { API_BASE } from "@/lib/api";

const ALL_TABS = [
  { k: "resellers", l: "Resellers", i: ShoppingBag, superadminOnly: false },
  { k: "influencers", l: "Influencers", i: Sparkles, superadminOnly: false },
  { k: "campaigns", l: "Campañas", i: Rocket, superadminOnly: false },
  { k: "reports", l: "Reportes", i: BarChart3, superadminOnly: false },
  { k: "white_label", l: "White-Label", i: Building2, superadminOnly: false },
  { k: "api_keys", l: "API Keys", i: KeyRound, superadminOnly: true },
];

export default function SuperadminAliados() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("superadmin_dcc");
  const TABS = ALL_TABS.filter((t) => !t.superadminOnly || isSuper);
  const [tab, setTab] = useState("resellers");
  return (
    <StaffLayout user={user} superadmin={isSuper}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Fase 7 · Distribución</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Aliados & canales</h1>
        <p className="mt-1 text-sm text-gray-400">Resellers, influencers, tenants white-label y API keys de distribución de contenido.</p>
      </div>
      <div role="tablist" className="flex flex-wrap gap-2 border-b border-gray-800 pb-4" data-testid="superadmin-aliados-tabs">
        {TABS.map((t) => {
          const active = tab === t.k;
          const Icon = t.i;
          return (
            <button
              key={t.k}
              role="tab"
              data-testid={`sa-tab-${t.k}`}
              aria-selected={active}
              onClick={() => setTab(t.k)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all inline-flex items-center gap-2 ${
                active ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300 hover:bg-white/10"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.l}
            </button>
          );
        })}
      </div>
      <div className="mt-6">
        {tab === "resellers" && <ResellersTab />}
        {tab === "influencers" && <InfluencersTab />}
        {tab === "campaigns" && <CampaignsTab />}
        {tab === "reports" && <ReportsTab />}
        {tab === "white_label" && <WhiteLabelTab />}
        {tab === "api_keys" && <ApiKeysTab />}
      </div>
    </StaffLayout>
  );
}

// ---------- Resellers ----------
function ResellersTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ slug: "", company_name: "", contact_name: "", contact_email: "", contact_phone: "", commission_pct: 20 });

  const load = () => api.get("/admin/resellers").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/resellers", form);
      toast.success(`Reseller ${form.slug} creado`);
      setCreating(false);
      setForm({ slug: "", company_name: "", contact_name: "", contact_email: "", contact_phone: "", commission_pct: 20 });
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-400">{items.length} resellers</p>
        <div className="flex gap-2">
          <InviteAliadoBtn type="reseller" onDone={load} />
          <button data-testid="sa-reseller-new-btn" onClick={() => setCreating((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 text-white font-semibold text-sm min-h-[44px]">
            <Plus className="w-4 h-4" /> {creating ? "Cancelar" : "Manual"}
          </button>
        </div>
      </div>
      {creating && (
        <form onSubmit={create} className="p-5 rounded-2xl bg-white/5 border border-white/10 grid md:grid-cols-2 gap-3 mb-6" data-testid="sa-reseller-form">
          <F label="Slug *" val={form.slug} on={(v) => setForm({ ...form, slug: v })} testid="rf-slug" />
          <F label="Empresa *" val={form.company_name} on={(v) => setForm({ ...form, company_name: v })} testid="rf-company" />
          <F label="Contacto *" val={form.contact_name} on={(v) => setForm({ ...form, contact_name: v })} testid="rf-contact" />
          <F label="Correo *" val={form.contact_email} on={(v) => setForm({ ...form, contact_email: v })} testid="rf-email" type="email" />
          <F label="Teléfono" val={form.contact_phone} on={(v) => setForm({ ...form, contact_phone: v })} testid="rf-phone" />
          <F label="% Comisión" val={String(form.commission_pct)} on={(v) => setForm({ ...form, commission_pct: parseFloat(v) || 0 })} testid="rf-pct" />
          <button type="submit" data-testid="rf-submit" className="md:col-span-2 mt-2 px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0B0B] font-semibold">Crear reseller</button>
        </form>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-gray-500">
            <tr><th className="py-2">Empresa</th><th>Slug</th><th>Comisión</th><th>Status</th><th>Contacto</th><th></th></tr>
          </thead>
          <tbody className="text-gray-300">
            {items.map((r) => (
              <tr key={r.id} data-testid={`reseller-row-${r.slug}`} className="border-t border-white/5">
                <td className="py-2 font-semibold text-white">{r.company_name}</td>
                <td className="font-mono text-xs">{r.slug}</td>
                <td>{r.commission_pct}%</td>
                <td>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusPill(r.status)}`}>{r.status}</span>
                </td>
                <td>{r.contact_name} · {r.contact_email}</td>
                <td className="py-2">
                  <AliadoRowActions aliado={r} type="reseller" onDone={load} />
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-6 text-gray-500 text-center">Sin resellers todavía.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Influencers ----------
function InfluencersTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/influencers").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-400">{items.length} influencers</p>
        <InviteAliadoBtn type="influencer" onDone={load} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((i) => (
          <article key={i.id} data-testid={`influencer-card-${i.slug}`} className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">{i.platform}</p>
            <h3 className="mt-1 font-bold text-white text-lg">{i.name}</h3>
            <p className="text-xs text-gray-400 mt-1">{i.handle} · {i.follower_range}</p>
            <div className="mt-3 flex items-center gap-2 text-xs flex-wrap">
              <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">{i.commission_pct}%</span>
              <span className={`px-2 py-0.5 rounded ${statusPill(i.status)}`}>{i.status}</span>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
              <a href={`/i/${i.landing_slug}`} target="_blank" rel="noreferrer"
                data-testid={`influencer-landing-link-${i.slug}`}
                className="inline-flex items-center gap-1 text-xs text-[#F26A21] hover:text-[#D9541A]">
                <ExternalLink className="w-3 h-3" /> /i/{i.landing_slug}
              </a>
              <AliadoRowActions aliado={i} type="influencer" onDone={load} />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function statusPill(status) {
  if (status === "active") return "bg-emerald-500/20 text-emerald-300";
  if (status === "pending_approval") return "bg-amber-500/20 text-amber-300";
  if (status === "rejected" || status === "suspended") return "bg-red-500/20 text-red-300";
  return "bg-gray-500/20 text-gray-300";
}

// ---------- White-Label ----------
function WhiteLabelTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/tenants/white-label").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const toggle = async (slug, enable) => {
    setBusy(slug);
    try {
      await api.put(`/admin/tenants/${slug}/white-label`, {
        is_white_label: enable,
        white_label_config: enable ? { show_dcc_footer: true } : {},
      });
      toast.success(`${slug} white-label ${enable ? "activado" : "desactivado"}`);
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    setBusy(null);
  };

  return (
    <div>
      <p className="text-sm text-gray-400 mb-4">{items.length} partners white-label</p>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((t) => (
          <article key={t.slug} data-testid={`wl-tenant-${t.slug}`} className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <h3 className="font-bold text-white text-lg">{t.name}</h3>
            <p className="text-xs text-gray-400 mt-1 font-mono">{t.slug}</p>
            <div className="mt-3 text-xs text-gray-300 space-y-1">
              <p><span className="text-gray-500">Contact email:</span> {t.white_label_config?.contact_email_override || "—"}</p>
              <p><span className="text-gray-500">Domain alias:</span> {t.white_label_config?.domain_alias || "—"}</p>
              <p><span className="text-gray-500">Show DCC footer:</span> {t.white_label_config?.show_dcc_footer !== false ? "sí" : "no"}</p>
            </div>
            <button onClick={() => toggle(t.slug, false)} disabled={busy === t.slug}
              data-testid={`wl-toggle-${t.slug}`}
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold">
              Desactivar white-label
            </button>
          </article>
        ))}
        {items.length === 0 && <p className="text-gray-500 col-span-2 text-center py-6">No hay tenants en modo white-label.</p>}
      </div>
    </div>
  );
}

// ---------- API Keys ----------
function ApiKeysTab() {
  const [items, setItems] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [showPlain, setShowPlain] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", env: "test", scopes: ["catalog:read"], rate_limit_per_min: 60 });

  const load = () => api.get("/admin/api-keys").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/api-keys", { ...form, owner_type: "external_platform" });
      setNewKey(data);
      setShowPlain(false);
      toast.success("API key generada. Copia el plaintext AHORA — no volverás a verlo.");
      load();
      setCreating(false);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revocar esta API key? Todas las peticiones futuras devolverán 401.")) return;
    try {
      await api.delete(`/admin/api-keys/${id}`);
      toast.success("API key revocada.");
      load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const toggleScope = (s) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));
  };

  return (
    <div>
      {newKey && (
        <div className="mb-6 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/40" data-testid="new-api-key-panel">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Nueva API key · Solo verás esto una vez</p>
          <div className="mt-2 flex items-center gap-2 font-mono text-sm text-white">
            <input readOnly type={showPlain ? "text" : "password"} value={newKey.plain}
              className="flex-1 px-3 py-2 rounded bg-black/40 border border-white/10 text-emerald-300" />
            <button onClick={() => setShowPlain((v) => !v)} data-testid="new-api-key-toggle" className="px-3 py-2 bg-white/5 rounded hover:bg-white/10">
              {showPlain ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={() => { navigator.clipboard.writeText(newKey.plain); toast.success("Copied"); }}
              data-testid="new-api-key-copy"
              className="px-3 py-2 bg-[#F26A21] rounded text-[#0B0B0B] hover:bg-[#D9541A]">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-3 text-xs text-gray-400 hover:text-white">Cerrar (ya copié la key)</button>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-400">{items.length} keys</p>
        <button data-testid="sa-apikey-new-btn" onClick={() => setCreating((v) => !v)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all">
          <Plus className="w-4 h-4" /> {creating ? "Cancelar" : "Nueva API key"}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="p-5 rounded-2xl bg-white/5 border border-white/10 grid md:grid-cols-2 gap-3 mb-6" data-testid="sa-apikey-form">
          <F label="Nombre *" val={form.name} on={(v) => setForm({ ...form, name: v })} testid="kf-name" />
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Entorno</span>
            <select value={form.env} onChange={(e) => setForm({ ...form, env: e.target.value })} data-testid="kf-env"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
              <option value="test">test</option>
              <option value="live">live</option>
            </select>
          </label>
          <F label="Rate limit / min" val={String(form.rate_limit_per_min)} on={(v) => setForm({ ...form, rate_limit_per_min: parseInt(v || "60", 10) })} testid="kf-rate" />
          <div className="md:col-span-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-2">Scopes</p>
            <div className="flex flex-wrap gap-2">
              {["catalog:read", "content:read", "enrollments:write", "certificates:issue"].map((s) => {
                const active = form.scopes.includes(s);
                return (
                  <button type="button" key={s} data-testid={`kf-scope-${s}`}
                    onClick={() => toggleScope(s)}
                    className={`px-3 py-1 rounded-full text-xs font-mono ${active ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300 border border-white/10"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
          <button type="submit" data-testid="kf-submit" className="md:col-span-2 mt-2 px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0B0B] font-semibold">Generar key</button>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-gray-500">
            <tr><th className="py-2">Nombre</th><th>Prefix</th><th>Scopes</th><th>Rate</th><th>Estado</th><th>Última uso</th><th></th></tr>
          </thead>
          <tbody className="text-gray-300">
            {items.map((k) => (
              <tr key={k.id} data-testid={`apikey-row-${k.prefix}`} className="border-t border-white/5">
                <td className="py-2 text-white font-semibold">{k.name}</td>
                <td className="font-mono text-xs">{k.prefix}...</td>
                <td className="text-xs">{k.scopes.join(", ")}</td>
                <td>{k.rate_limit_per_min}/m</td>
                <td>{k.revoked_at ? <span className="text-red-400">Revocada</span> : <span className="text-emerald-400">Activa</span>}</td>
                <td className="text-xs">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "nunca"}</td>
                <td>
                  {!k.revoked_at && (
                    <button onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.prefix}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/20 hover:bg-red-500/30 text-red-300">
                      <Ban className="w-3 h-3" /> Revocar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="py-6 text-gray-500 text-center">Sin API keys generadas.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function F({ label, val, on, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} type={type} data-testid={testid}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}

// ============ FASE 8 COMPONENTS ============

// ---------- Invite aliado modal ----------
function InviteAliadoBtn({ type, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={`invite-${type}-btn`}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]"
      >
        <Mail className="w-4 h-4" /> Invitar por email
      </button>
      {open && <InviteAliadoModal type={type} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone?.(); }} />}
    </>
  );
}

function InviteAliadoModal({ type, onClose, onDone }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [commissionPct, setCommissionPct] = useState(type === "reseller" ? 20 : 10);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email || !name) return toast.error("Email y nombre requeridos");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/aliados/invite", {
        email, type, name,
        commission_pct: parseFloat(commissionPct) || 0,
        notes: notes || undefined,
      });
      setResult(data);
      toast.success("Invitación enviada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.onboarding_url);
    toast.success("Link copiado");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`invite-${type}-modal`}
        className="w-full max-w-lg bg-[#111827] border border-white/10 rounded-2xl p-6"
      >
        <h2 className="text-xl font-bold text-white font-display">Invitar {type === "reseller" ? "reseller" : "influencer"}</h2>
        <p className="text-xs text-gray-400 mt-1">El aliado recibe un email con un link que expira en 7 días.</p>

        {!result ? (
          <>
            <div className="mt-5 space-y-3">
              <F label="Email *" val={email} on={setEmail} testid="inv-email" type="email" />
              <F label="Nombre *" val={name} on={setName} testid="inv-name" />
              <F label="% Comisión" val={String(commissionPct)} on={setCommissionPct} testid="inv-pct" />
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Notas internas</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                  data-testid="inv-notes"
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
              <button onClick={submit} disabled={busy} data-testid="inv-submit-btn"
                className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
                {busy ? "Enviando…" : "Enviar invitación"}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-6">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/40 rounded-xl">
              <p className="text-sm text-emerald-300 font-semibold">✓ Invitación enviada a {result.email}</p>
              <p className="mt-2 text-xs text-gray-400">Link de onboarding (por si el email falla):</p>
              <div className="mt-2 flex items-center gap-2">
                <code data-testid="invite-link" className="flex-1 text-[10px] font-mono text-emerald-200 bg-black/40 px-2 py-2 rounded truncate">
                  {result.onboarding_url}
                </code>
                <button onClick={copyLink} className="p-2 text-gray-300 hover:bg-white/5 rounded" data-testid="copy-invite-link">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">Expira: {new Date(result.expires_at).toLocaleString("es-MX")}</p>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={onDone} className="px-6 py-2 rounded-md bg-white/10 text-white text-sm min-h-[44px]" data-testid="invite-done-btn">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Row actions: Permisos, Aprobar/Rechazar ----------
function AliadoRowActions({ aliado, type, onDone }) {
  const [showPerms, setShowPerms] = useState(false);
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("superadmin_dcc");

  const doAction = async (action, reason) => {
    try {
      const url = `/admin/${type}s/${aliado.id}/${action}`;
      await api.put(url, { reason });
      toast.success(action === "approve" ? "Aliado aprobado" : "Aliado rechazado");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button onClick={() => setShowPerms(true)}
        data-testid={`perms-btn-${aliado.id}`}
        title="Permisos"
        className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
        <Settings className="w-4 h-4" />
      </button>
      {aliado.status === "pending_approval" && isSuper && (
        <>
          <button onClick={() => doAction("approve")}
            data-testid={`approve-btn-${aliado.id}`}
            className="p-2 text-emerald-400 hover:bg-emerald-500/10 rounded min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
            <Check className="w-4 h-4" />
          </button>
          <button onClick={() => {
              const r = prompt("Motivo de rechazo:");
              if (r) doAction("reject", r);
            }}
            data-testid={`reject-btn-${aliado.id}`}
            className="p-2 text-red-400 hover:bg-red-500/10 rounded min-w-[44px] min-h-[44px] inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </>
      )}
      {showPerms && <PermissionsModal aliado={aliado} type={type} onClose={() => setShowPerms(false)} onDone={() => { setShowPerms(false); onDone?.(); }} />}
    </div>
  );
}

// ---------- Permissions modal ----------
function PermissionsModal({ aliado, type, onClose, onDone }) {
  const [programs, setPrograms] = useState([]);
  const [allowed, setAllowed] = useState(aliado.allowed_program_ids || []);
  const [canCreateDiscount, setCanCreateDiscount] = useState(!!aliado.can_create_discount_codes);
  const [whiteLabelEnabled, setWhiteLabelEnabled] = useState(!!aliado.white_label_enabled);
  const [payoutMethod, setPayoutMethod] = useState(aliado.payout_method || "manual");
  const [notes, setNotes] = useState(aliado.notes || "");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/catalog/programs", { params: { limit: 500 } }).then((r) => setPrograms(r.data.items || []));
  }, []);

  const filtered = useMemo(() => {
    if (!q) return programs;
    const qq = q.toLowerCase();
    return programs.filter((p) => p.title?.toLowerCase().includes(qq) || p.code?.toLowerCase().includes(qq));
  }, [programs, q]);

  const toggle = (pid) => {
    setAllowed((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const url = `/admin/${type}s/${aliado.id}/permissions`;
      await api.put(url, {
        allowed_program_ids: allowed,
        can_create_discount_codes: canCreateDiscount,
        white_label_enabled: whiteLabelEnabled,
        payout_method: payoutMethod,
        notes,
      });
      toast.success("Permisos actualizados");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const name = aliado.company_name || aliado.name;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="permissions-modal"
        className="w-full max-w-2xl bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <h2 className="text-xl font-bold text-white font-display">Permisos · {name}</h2>
        <p className="text-xs text-gray-400 mt-1">Configura alcance, códigos y payouts.</p>

        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          <Toggle label="Puede crear códigos de descuento" val={canCreateDiscount} on={setCanCreateDiscount} tid="perm-cando-discounts" />
          <Toggle label="White-label habilitado (reseller premium)" val={whiteLabelEnabled} on={setWhiteLabelEnabled} tid="perm-wl-enabled" />
        </div>

        <div className="mt-4">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Método de pago</label>
          <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)}
            data-testid="perm-payout-method"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white">
            {[["bank_transfer", "Transferencia bancaria"], ["spei", "SPEI"], ["manual", "Manual"]].map(([v, l]) =>
              <option key={v} value={v}>{l}</option>
            )}
          </select>
        </div>

        <div className="mt-4">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Notas CRM (internas)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            data-testid="perm-notes"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
        </div>

        <div className="mt-6">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
            Programas permitidos ({allowed.length === 0 ? "todos" : `${allowed.length} de ${programs.length}`})
          </label>
          <input placeholder="Buscar programa..." value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="perm-search"
            className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          <div className="mt-2 max-h-64 overflow-y-auto border border-white/5 rounded-md">
            {filtered.slice(0, 50).map((p) => (
              <label key={p.id} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-white/5 cursor-pointer">
                <input type="checkbox" checked={allowed.includes(p.id)} onChange={() => toggle(p.id)}
                  data-testid={`perm-prog-${p.code}`} />
                <span className="font-mono text-[10px] text-gray-500 w-14">{p.code}</span>
                <span>{p.title}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="p-3 text-center text-xs text-gray-500">Sin resultados.</p>}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="perm-save-btn"
            className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            {busy ? "Guardando…" : "Guardar permisos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, val, on, tid }) {
  return (
    <label className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer">
      <input type="checkbox" checked={val} onChange={(e) => on(e.target.checked)} data-testid={tid} className="mt-1" />
      <span className="text-xs text-gray-200 leading-relaxed">{label}</span>
    </label>
  );
}

// ---------- Campaigns tab ----------
function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [openReport, setOpenReport] = useState(null);
  const load = () => api.get("/admin/campaigns").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-400">{items.length} campañas</p>
        <button onClick={() => setCreating(true)}
          data-testid="campaigns-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
          <Plus className="w-4 h-4" /> Nueva campaña
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((c) => (
          <article key={c.id} data-testid={`campaign-card-${c.slug}`} className="p-5 rounded-2xl bg-white/5 border border-white/10">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">{c.status}</p>
                <h3 className="mt-1 font-bold text-white text-lg">{c.name}</h3>
                <p className="text-xs text-gray-400 mt-1">{c.start_date} → {c.end_date}</p>
              </div>
              <button
                onClick={() => setOpenReport(c)}
                data-testid={`campaign-report-btn-${c.slug}`}
                className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md bg-white/10 text-white hover:bg-white/20 min-h-[36px]">
                <BarChart3 className="w-3 h-3" /> Reporte
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-0.5 rounded bg-white/5 text-gray-300">{c.program_ids.length} programas</span>
              <span className="px-2 py-0.5 rounded bg-white/5 text-gray-300">{c.participating_aliados.length} aliados</span>
              {c.budget_mxn && <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300">${c.budget_mxn.toLocaleString()} MXN</span>}
            </div>
            <p className="mt-3 text-xs text-gray-400 line-clamp-2">{c.description}</p>
          </article>
        ))}
        {items.length === 0 && <p className="text-sm text-gray-500 col-span-2">Sin campañas aún.</p>}
      </div>
      {creating && <NewCampaignModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {openReport && <CampaignReportModal campaign={openReport} onClose={() => setOpenReport(null)} />}
    </div>
  );
}

function NewCampaignModal({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date(Date.now() + 90 * 86400_000).toISOString().slice(0, 10));
  const [budget, setBudget] = useState("");
  const [target, setTarget] = useState("");
  const [utm, setUtm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name || !startDate || !endDate) return toast.error("Nombre + fechas requeridos");
    setBusy(true);
    try {
      await api.post("/admin/campaigns", {
        name, description,
        start_date: startDate, end_date: endDate,
        budget_mxn: budget ? parseFloat(budget) : undefined,
        target_conversions: target ? parseInt(target) : undefined,
        utm_prefix: utm || undefined,
        status: "active",
      });
      toast.success("Campaña creada");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="new-campaign-modal"
        className="w-full max-w-xl bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white font-display">Nueva campaña</h2>
        <div className="mt-5 space-y-3">
          <F label="Nombre *" val={name} on={setName} testid="camp-name" />
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              data-testid="camp-desc"
              className="w-full mt-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Inicio *" val={startDate} on={setStartDate} testid="camp-start" type="date" />
            <F label="Fin *" val={endDate} on={setEndDate} testid="camp-end" type="date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Budget MXN" val={budget} on={setBudget} testid="camp-budget" />
            <F label="Target conversiones" val={target} on={setTarget} testid="camp-target" />
          </div>
          <F label="UTM prefix" val={utm} on={setUtm} testid="camp-utm" />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/10 text-gray-300 text-sm min-h-[44px]">Cancelar</button>
          <button onClick={submit} disabled={busy} data-testid="camp-submit-btn"
            className="px-6 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] disabled:opacity-50 text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            {busy ? "Creando…" : "Crear"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CampaignReportModal({ campaign, onClose }) {
  const [report, setReport] = useState(null);
  useEffect(() => {
    api.get(`/admin/campaigns/${campaign.id}/report`).then((r) => setReport(r.data));
  }, [campaign.id]);
  if (!report) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center" onClick={onClose}>
        <p className="text-white">Cargando reporte…</p>
      </div>
    );
  }
  const funnel = [
    { name: "Clicks", v: report.funnel.clicks },
    { name: "Carrito", v: report.funnel.cart_attaches },
    { name: "Checkout", v: report.funnel.checkouts_started },
    { name: "Compra", v: report.funnel.purchases },
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="campaign-report-modal"
        className="w-full max-w-4xl bg-[#111827] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">Reporte de campaña</p>
            <h2 className="mt-1 text-xl font-bold text-white font-display">{campaign.name}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 p-2 min-w-[44px] min-h-[44px]"><X /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatMini label="Revenue" value={`$${report.revenue_mxn.toLocaleString()}`} />
          <StatMini label="Comisiones" value={`$${report.commissions_mxn.toLocaleString()}`} />
          <StatMini label="Conversión" value={`${report.conversion_rate_pct}%`} />
          <StatMini label="ROAS" value={report.roas ? `${report.roas}x` : "—"} accent />
        </div>
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-4" data-testid="report-funnel-chart">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Funnel de conversión</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={funnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="name" stroke="#9CA3AF" fontSize={11} />
              <YAxis stroke="#9CA3AF" fontSize={11} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", color: "#E5E7EB" }} />
              <Bar dataKey="v" fill="#F26A21" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {report.per_aliado.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/10">
                <tr><th className="py-2">Aliado</th><th>Clicks</th><th>Compras</th><th>Revenue</th><th>Comisión</th></tr>
              </thead>
              <tbody className="text-gray-300">
                {report.per_aliado.map((a, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="py-2 capitalize">{a.aliado_type} · {a.aliado_id.slice(0, 8)}…</td>
                    <td>{a.clicks}</td>
                    <td>{a.purchases}</td>
                    <td>${a.revenue_mxn.toLocaleString()}</td>
                    <td>${a.commission_mxn.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatMini({ label, value, accent }) {
  return (
    <div className={`p-4 rounded-xl border ${accent ? "bg-[#F26A21]/10 border-[#F26A21]/40" : "bg-white/5 border-white/10"}`}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
      <p className="mt-1 text-lg font-bold text-white">{value}</p>
    </div>
  );
}

// ---------- Reports tab (global) ----------
function ReportsTab() {
  const [data, setData] = useState(null);
  const [ownerType, setOwnerType] = useState("");
  useEffect(() => {
    const params = {};
    if (ownerType) params.owner_type = ownerType;
    api.get("/admin/aliados/reports/global", { params }).then((r) => setData(r.data));
  }, [ownerType]);
  if (!data) return <p className="text-gray-400">Cargando…</p>;
  const token = localStorage.getItem("dcc_access_token");
  const csvUrl = `${API_BASE}/admin/aliados/reports/global.csv?token=${token}`;
  const xlsxUrl = `${API_BASE}/admin/aliados/reports/global.xlsx?token=${token}`;
  const downloadWithAuth = async (path, filename) => {
    const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("No se pudo descargar");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2">
          <button onClick={() => setOwnerType("")} data-testid="rep-filter-all"
            className={`text-xs px-3 py-1 rounded-full ${ownerType === "" ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300"}`}>Todos</button>
          <button onClick={() => setOwnerType("reseller")} data-testid="rep-filter-reseller"
            className={`text-xs px-3 py-1 rounded-full ${ownerType === "reseller" ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300"}`}>Resellers</button>
          <button onClick={() => setOwnerType("influencer")} data-testid="rep-filter-influencer"
            className={`text-xs px-3 py-1 rounded-full ${ownerType === "influencer" ? "bg-[#F26A21] text-[#0B0B0B]" : "bg-white/5 text-gray-300"}`}>Influencers</button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadWithAuth("/admin/aliados/reports/global.csv", "dcc-aliados.csv")}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-white text-xs min-h-[44px]">
            <Download className="w-3 h-3" /> CSV
          </button>
          <button onClick={() => downloadWithAuth("/admin/aliados/reports/global.xlsx", "dcc-aliados.xlsx")}
            data-testid="export-xlsx-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-white text-xs min-h-[44px]">
            <Download className="w-3 h-3" /> XLSX
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatMini label="Ventas totales" value={`$${data.total_sales_mxn.toLocaleString()}`} />
        <StatMini label="Comisiones" value={`$${data.total_commissions_mxn.toLocaleString()}`} />
        <StatMini label="Pagadas" value={`$${data.paid_commissions_mxn.toLocaleString()}`} />
        <StatMini label="Órdenes" value={data.orders_count} accent />
      </div>
      {data.trend_90d.length > 0 && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 mb-6" data-testid="report-trend-chart">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">Tendencia últimos 90 días</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.trend_90d}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
              <XAxis dataKey="date" stroke="#9CA3AF" fontSize={10} />
              <YAxis stroke="#9CA3AF" fontSize={10} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", color: "#E5E7EB" }} />
              <Area type="monotone" dataKey="revenue_mxn" stroke="#F26A21" fill="#F26A21" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">
          <TrendingUp className="inline w-3 h-3 mr-1" />
          Top 5 aliados por ventas
        </p>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm" data-testid="reports-top-aliados-table">
            <thead className="bg-white/5 text-left text-[10px] uppercase tracking-widest text-gray-500">
              <tr><th className="p-3">Aliado</th><th className="p-3">Tipo</th><th className="p-3">Ventas MXN</th><th className="p-3">Comisiones MXN</th><th className="p-3">Órdenes</th></tr>
            </thead>
            <tbody className="text-gray-300">
              {data.top_aliados.map((a, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="p-3 font-semibold text-white">{a.display_name}</td>
                  <td className="p-3 capitalize">{a.owner_type}</td>
                  <td className="p-3">${a.sales_mxn.toLocaleString()}</td>
                  <td className="p-3">${a.commissions_mxn.toLocaleString()}</td>
                  <td className="p-3">{a.orders}</td>
                </tr>
              ))}
              {data.top_aliados.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Sin datos con estos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
